import { createAuthGuard } from '@auth0/auth0-vue'
import { createRouter, createWebHistory } from 'vue-router'
import AccountDetails from '../views/AccountDetails.vue'
import HomeView from '../views/HomeView.vue'
import LoginView from '../views/LoginView.vue'
import MonitorDetails from '../views/MonitorDetails.vue'
import MonitorLiveStream from '../views/MonitorLiveStream.vue'
import VesselActionHistory from '../views/VesselActionHistory.vue'
import VesselChemCheckHistory from '../views/VesselChemCheckHistory.vue'
import VesselDetails from '../views/VesselDetails.vue'
import ProtocolInfo from '../views/engineering/ProtocolInfo.vue'
import VesselStatusUpdateLambdaLogs from '../views/engineering/VesselStatusUpdateLambdaLogs.vue'
import MonitorCalibrations from '../views/monitors/MonitorCalibrations.vue'
import MonitorJobExecutionDetails from '../views/monitors/MonitorJobExecutionDetails.vue'
import SensorDetails from '../views/sensors/SensorDetails.vue'
import WaterReportHistory from '../views/waterReports/WaterReportHistory.vue'
import GeckoSpaStatus from '@/views/gecko/GeckoSpaStatus.vue'
import VesselAnalytics from '../views/analytics/VesselAnalytics.vue'
import TestStripAnalytics from '../views/analytics/TestStripAnalytics.vue'
import AccountTroubleShootingUploadDetails from '../views/troubleshooting/AccountTroubleShootingUploadDetails.vue'
import AccountTroubleShootingUploads from '../views/troubleshooting/AccountTroubleShootingUploads.vue'
import GeckoApp from '../views/GeckoApp.vue'

const authGuard = createAuthGuard()

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        {
            path: '/login',
            name: 'login',
            component: LoginView
        },
        {
            path: '/',
            name: 'home',
            component: HomeView,
            beforeEnter: authGuard
        },
        {
            path: '/gecko-app',
            name: 'GeckoApp',
            component: GeckoApp,
            beforeEnter: authGuard
        },
        {
            path: '/accounts/:accountId',
            name: 'AccountDetails',
            component: AccountDetails,
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.accountId)) throw Error(`accountId cannot be an array`)
                const accountId = parseInt(route.params.accountId, 10)
                return { accountId }
            }
        },
        {
            path: '/accounts/:accountId/vessels/:vesselId',
            name: 'VesselDetails',
            component: VesselDetails,
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.accountId)) throw Error(`accountId cannot be an array`)
                if (Array.isArray(route.params.vesselId)) throw Error(`vesselId cannot be an array`)
                const accountId = parseInt(route.params.accountId, 10)
                const vesselId = parseInt(route.params.vesselId, 10)
                return { accountId, vesselId }
            },
            meta: {
                showSharedVesselInfoBanner: true
            }
        },
        {
            path: '/accounts/:accountId/vessels/:vesselId/actions',
            name: 'VesselActionHistory',
            component: VesselActionHistory,
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.accountId)) throw Error(`accountId cannot be an array`)
                if (Array.isArray(route.params.vesselId)) throw Error(`vesselId cannot be an array`)
                const accountId = parseInt(route.params.accountId, 10)
                const vesselId = parseInt(route.params.vesselId, 10)
                return { accountId, vesselId }
            },
            meta: {
                showSharedVesselInfoBanner: true
            }
        },
        {
            path: '/accounts/:accountId/vessels/:vesselId/waterReports',
            name: 'WaterReportHistory',
            component: WaterReportHistory,
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.accountId)) throw Error(`accountId cannot be an array`)
                if (Array.isArray(route.params.vesselId)) throw Error(`vesselId cannot be an array`)
                const accountId = parseInt(route.params.accountId, 10)
                const vesselId = parseInt(route.params.vesselId, 10)
                return { accountId, vesselId }
            },
            meta: {
                showSharedVesselInfoBanner: true
            }
        },
        {
            path: '/accounts/:accountId/vessels/:vesselId/chemCheck/history',
            name: 'VesselChemCheckHistory',
            component: VesselChemCheckHistory,
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.accountId)) throw Error(`accountId cannot be an array`)
                if (Array.isArray(route.params.vesselId)) throw Error(`vesselId cannot be an array`)
                const accountId = parseInt(route.params.accountId, 10)
                const vesselId = parseInt(route.params.vesselId, 10)
                return { accountId, vesselId }
            },
            meta: {
                showSharedVesselInfoBanner: true
            }
        },
        {
            path: '/accounts/:accountId/vessels/:vesselId/sessions/history',
            name: 'VesselSessionHistory',
            component: () => import('../views/VesselSessionHistory.vue'),
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.accountId)) throw new Error(`accountId cannot be an array`)
                if (Array.isArray(route.params.vesselId)) throw new Error(`vesselId cannot be an array`)
                const accountId = Number.parseInt(route.params.accountId, 10)
                const vesselId = Number.parseInt(route.params.vesselId, 10)
                return { accountId, vesselId }
            },
            meta: {
                showSharedVesselInfoBanner: true
            }
        },
        {
            path: '/accounts/:accountId/vessels/:vesselId/protocol/info',
            name: 'ProtocolInfo',
            component: ProtocolInfo,
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.accountId)) throw Error(`accountId cannot be an array`)
                if (Array.isArray(route.params.vesselId)) throw Error(`vesselId cannot be an array`)
                const accountId = parseInt(route.params.accountId, 10)
                const vesselId = parseInt(route.params.vesselId, 10)
                return { accountId, vesselId }
            },
            meta: {
                showSharedVesselInfoBanner: true
            }
        },
        {
            path: '/accounts/:accountId/vessels/:vesselId/chemCheck/history/:requestId/:eventDate/lambdaLogs',
            name: 'VesselStatusUpdateLambdaLogs',
            component: VesselStatusUpdateLambdaLogs,
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.accountId)) throw Error(`accountId cannot be an array`)
                if (Array.isArray(route.params.vesselId)) throw Error(`vesselId cannot be an array`)
                const accountId = parseInt(route.params.accountId, 10)
                const vesselId = parseInt(route.params.vesselId, 10)
                const awsRequestId = route.params.requestId
                const eventDate = route.params.eventDate
                return { accountId, vesselId, awsRequestId, eventDate }
            }
        },
        {
            path: '/accounts/:accountId/troubleshooting/uploads',
            name: 'AccountTroubleShootingUploads',
            component: AccountTroubleShootingUploads,
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.accountId)) throw Error(`accountId cannot be an array`)
                const accountId = parseInt(route.params.accountId, 10)
                return { accountId }
            }
        },
        {
            path: '/accounts/:accountId/troubleshooting/uploads/:uploadId',
            name: 'AccountTroubleShootingUploadDetails',
            component: AccountTroubleShootingUploadDetails,
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.accountId)) throw Error(`accountId cannot be an array`)
                if (Array.isArray(route.params.uploadId)) throw Error(`uploadId cannot be an array`)
                const accountId = parseInt(route.params.accountId, 10)
                const uploadId = parseInt(route.params.uploadId, 10)
                return { accountId, uploadId }
            }
        },
        {
            path: '/monitors/:monitorId',
            name: 'MonitorDetails',
            component: MonitorDetails,
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.monitorId)) throw Error(`monitorId cannot be an array`)
                const monitorId = route.params.monitorId
                return { monitorId }
            }
        },
        {
            path: '/monitors/:monitorId/jobs/:jobId/executions/:executionNumber',
            name: 'MonitorJobExecutionDetails',
            component: MonitorJobExecutionDetails,
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.monitorId)) throw Error(`monitorId cannot be an array`)
                if (Array.isArray(route.params.jobId)) throw Error(`jobId cannot be an array`)
                if (Array.isArray(route.params.executionNumber)) throw Error(`executionNumber cannot be an array`)

                const { monitorId, jobId, executionNumber } = route.params
                return { monitorId, jobId, executionNumber: parseInt(executionNumber, 10) }
            }
        },
        {
            path: '/monitors/:monitorId/live',
            name: 'MonitorLiveStream',
            component: MonitorLiveStream,
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.monitorId)) throw Error(`monitorId cannot be an array`)
                const monitorId = route.params.monitorId
                return { monitorId }
            }
        },
        {
            path: '/monitors/:monitorId/calibrations',
            name: 'MonitorCalibrations',
            component: MonitorCalibrations,
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.monitorId)) throw Error(`monitorId cannot be an array`)
                const monitorId = route.params.monitorId
                return { monitorId }
            }
        },
        {
            path: '/sensors/:serialNumber',
            name: 'SensorDetails',
            component: SensorDetails,
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.monitorId)) throw Error(`serialNumber cannot be an array`)
                const serialNumber = route.params.serialNumber
                return { serialNumber }
            }
        },
        {
            path: '/chemicals/account',
            name: 'AccountChemicals',
            component: () => import('../views/chemicals/AccountChemicals.vue'),
            beforeEnter: authGuard
        },
        {
            path: '/chemicals/account/:barcode/promote',
            name: 'PromoteAccountChemical',
            component: () => import('../views/chemicals/PromoteAccountChemical.vue'),
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.barcode)) throw Error(`barcode cannot be an array`)
                const barcode = route.params.barcode
                return { barcode }
            }
        },
        {
            path: '/chemicals/system',
            name: 'AllSystemChemicals',
            component: () => import('../views/chemicals/AllSystemChemicals.vue'),
            beforeEnter: authGuard
        },
        {
            path: '/chemicals/system/:systemChemicalId',
            name: 'SystemChemicalDetails',
            component: () => import('../views/chemicals/SystemChemicalDetails.vue'),
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.systemChemicalId)) throw Error(`systemChemicalId cannot be an array`)
                return { systemChemicalId: parseInt(route.params.systemChemicalId, 10) }
            }
        },
        {
            path: '/engineering/cloudwatch',
            name: 'CloudWatch',
            component: () => import('../views/engineering/CloudWatch.vue'),
            beforeEnter: authGuard
        },
        {
            path: '/engineering/recentteststrips',
            name: 'RecentTestStrips',
            component: () => import('../views/engineering/RecentTestStrips.vue'),
            beforeEnter: authGuard
        },
        {
            path: '/engineering/recentfeedback',
            name: 'RecentFeedback',
            component: () => import('../views/engineering/RecentFeedback.vue'),
            beforeEnter: authGuard
        },
        {
            path: '/engineering/shoveler',
            name: 'VesselStatusUpdateShoveler',
            component: () => import('../views/engineering/VesselStatusUpdateShoveler.vue'),
            beforeEnter: authGuard
        },
        {
            path: '/engineering/returnedMonitors',
            name: 'ReturnedMonitorsScan',
            component: () => import('../views/engineering/ReturnedMonitorsScan.vue'),
            beforeEnter: authGuard
        },
        {
            path: '/engineering/replacementMonitors',
            name: 'ReplacementMonitors',
            component: () => import('../views/engineering/ReplacementMonitors.vue'),
            beforeEnter: authGuard
        },
        {
            path: '/engineering/testStrips/catalog',
            name: 'TestStripCatalog',
            component: () => import('../views/engineering/TestStripCatalog.vue'),
            beforeEnter: authGuard
        },
        {
            path: '/engineering/accounts/:accountId/vessels/:vesselId/compareStatuses',
            name: 'CompareVesselStatuses',
            component: () => import('../views/engineering/CompareVesselStatuses.vue'),
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.accountId)) throw Error(`accountId cannot be an array`)
                if (Array.isArray(route.params.vesselId)) throw Error(`vesselId cannot be an array`)
                const accountId = parseInt(route.params.accountId, 10)
                const vesselId = parseInt(route.params.vesselId, 10)
                return { accountId, vesselId }
            }
        },
        {
            path: '/accounts/:accountId/vessels/:vesselId/gecko/spa',
            name: 'GeckoSpaStatus',
            component: GeckoSpaStatus,
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.accountId)) throw new Error(`accountId cannot be an array`)
                if (Array.isArray(route.params.vesselId)) throw new Error(`vesselId cannot be an array`)
                const accountId = Number.parseInt(route.params.accountId, 10)
                const vesselId = Number.parseInt(route.params.vesselId, 10)
                return { accountId, vesselId }
            }
        },
        {
            path: '/monitors/:monitorId/gecko/spa',
            name: 'MonitorGeckoSpaStatus',
            component: GeckoSpaStatus,
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.monitorId)) throw new Error(`monitorId cannot be an array`)
                return { monitorIdProp: route.params.monitorId }
            }
        },
        {
            path: '/accounts/:accountId/vessels/:vesselId/schedules',
            name: 'VesselSchedulesCalendar',
            component: () => import('../views/VesselSchedulesCalendar.vue'),
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.accountId)) throw new Error(`accountId cannot be an array`)
                if (Array.isArray(route.params.vesselId)) throw new Error(`vesselId cannot be an array`)
                const accountId = Number.parseInt(route.params.accountId, 10)
                const vesselId = Number.parseInt(route.params.vesselId, 10)
                return { accountId, vesselId }
            },
            meta: {
                showSharedVesselInfoBanner: true
            }
        },
        {
            path: '/accounts/:accountId/vessels/:vesselId/monitor/:monitorId/analytics',
            name: 'VesselAnalytics',
            component: VesselAnalytics,
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.accountId)) throw Error(`accountId cannot be an array`)
                if (Array.isArray(route.params.vesselId)) throw Error(`vesselId cannot be an array`)
                if (Array.isArray(route.params.monitorId)) throw Error(`monitorId cannot be an array`)
                const accountId = parseInt(route.params.accountId, 10)
                const vesselId = parseInt(route.params.vesselId, 10)
                const monitorId = route.params.monitorId
                return { accountId, vesselId, monitorId }
            }
        },
        {
            path: '/monitors/:monitorId/analytics',
            name: 'MonitorAnalytics',
            component: VesselAnalytics,
            beforeEnter: authGuard,
            props: (route) => {
                if (Array.isArray(route.params.monitorId)) throw new Error(`monitorId cannot be an array`)
                return { monitorId: route.params.monitorId }
            }
        },
        {
            path: '/analytics/test-strips',
            name: 'TestStripAnalytics',
            component: TestStripAnalytics,
            beforeEnter: authGuard
        },
        {
            path: '/help-center',
            name: 'HelpCenter',
            component: () => import('../views/HelpCenter.vue'),
            beforeEnter: authGuard
        },
        {
            path: '/tags',
            name: 'Tags',
            component: () => import('../views/tags/TagsView.vue'),
            beforeEnter: authGuard
        }
    ]
})

export default router
